// QuestionTrail — the assistant's question once it is settled, as one trail
// line in the thread (pen "Chat – every entry" §4): answered ("You answered 3
// questions", opens to question – answer) or skipped ("You skipped the
// questions" · Answer now).

import { msg, plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../../Icon/Icon.js";
import { TrailLine } from "../TrailLine/TrailLine.js";
import {
    type AnsweredFieldSummary,
    FALLBACK_FIELD_KEY,
    OWN_ANSWER_FIELD_KEY,
} from "../types.js";
import "./QuestionTrail.css";

export const AnsweredQuestions = ({
    answers,
}: {
    readonly answers: readonly AnsweredFieldSummary[];
}): ReactNode => {
    const { i18n } = useLingui();
    const count = answers.length;
    // Your own words read as they are; a field's own question comes first.
    const line = (item: AnsweredFieldSummary): string =>
        item.key === OWN_ANSWER_FIELD_KEY || item.key === FALLBACK_FIELD_KEY
            ? item.display
            : `${item.label} – ${item.display}`;
    return (
        <TrailLine
            glyph="answered"
            text={i18n._(
                msg`${plural(count, { one: "You answered the question", other: "You answered # questions" })}`,
            )}
        >
            {count === 0
                ? null
                : answers.map((item) => (
                      <div key={item.key} className="bilda-qtrail__answer">
                          <Icon
                              name="answer-line"
                              className="bilda-qtrail__icon"
                          />
                          <span>{line(item)}</span>
                      </div>
                  ))}
        </TrailLine>
    );
};

export const SkippedQuestions = ({
    onAnswerNow,
}: {
    readonly onAnswerNow: () => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <TrailLine
            glyph="skipped"
            text={t`You skipped the questions`}
            action={{ label: t`Answer now`, onAction: onAnswerNow }}
        />
    );
};
